/**
 * ResponsiveHeader - responsive header with mobile menu toggle
 */

import { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@stores/authStore';
import MobileNav from './MobileNav';
import { NotificationBell, NotificationDropdown } from '@components/notifications';
import { LogoIcon } from '@components/brand/Logo';

export default function ResponsiveHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, currentOrganization } = useAuthStore();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const navItems = [
    { to: '/dashboard', label: t('nav.dashboard') },
    { to: '/projects', label: t('nav.projects') },
    { to: '/meetings', label: t('nav.meetings') },
    { to: '/emails', label: t('nav.emails') },
    { to: '/reports', label: t('nav.reports') },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearchClick = () => {
    navigate('/search');
  };

  // Global keyboard shortcut for search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        // Don't hijack Cmd/Ctrl+K while the user is typing in a text field or
        // rich-text editor (where it conventionally inserts a link).
        const target = e.target as HTMLElement | null;
        if (target?.closest('input, textarea, [contenteditable="true"]')) {
          return;
        }
        e.preventDefault();
        navigate('/search');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  return (
    <>
      <header className="glass-effect-strong border-b border-neutral-200/50 dark:border-neutral-800/50 sticky top-0 z-[60] shadow-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 animate-slide-down">
            {/* Left: Logo & Mobile Menu Button */}
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileNavOpen(true)}
                className="lg:hidden p-2 hover:bg-primary-50/80 dark:hover:bg-primary-950/30 rounded-xl transition-all duration-200"
                aria-label="Open menu"
              >
                <svg className="w-6 h-6 text-neutral-700 dark:text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {/* Logo */}
              <Link to="/dashboard" className="flex items-center gap-2 sm:gap-2.5 group">
                <div className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                  <LogoIcon size={28} className="sm:w-8 sm:h-8" />
                </div>
                <span className="hidden xs:block font-display font-semibold text-base sm:text-lg tracking-tight">
                  <span className="text-primary-600 dark:text-primary-400">Neo</span>
                  <span className="text-neutral-900 dark:text-white">Kanban</span>
                </span>
              </Link>
            </div>

            {/* Center: Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `px-3.5 py-2 text-sm font-medium rounded-xl transition-all duration-200 relative group ${
                      isActive
                        ? 'text-primary-600 bg-primary-50/80 dark:text-primary-400 dark:bg-primary-950/30'
                        : 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50/80 dark:text-neutral-300 dark:hover:text-primary-400 dark:hover:bg-primary-950/30'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      <span
                        className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-primary-600 transition-all duration-300 ${
                          isActive ? 'w-3/4' : 'w-0 group-hover:w-3/4'
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Right: Actions & User Menu */}
            <div className="flex items-center gap-2">
              {/* Search - icon-only below md, expands to full search bar from md up */}
              <button
                onClick={handleSearchClick}
                aria-label={t('nav.search')}
                className="flex items-center gap-2.5 p-2 md:px-3.5 md:py-2 bg-neutral-50/80 dark:bg-neutral-800/50 border border-neutral-200/50 dark:border-neutral-700/50 rounded-xl text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100/80 dark:hover:bg-neutral-700/50 hover:border-primary-300/50 dark:hover:border-primary-700/50 transition-all duration-200 group"
              >
                <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="hidden lg:inline font-medium">{t('nav.search')}</span>
                <kbd className="hidden lg:inline px-2 py-0.5 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md text-xs font-mono shadow-sm">⌘K</kbd>
              </button>

              {/* Notifications */}
              <div className="relative">
                <NotificationBell
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  isOpen={notificationsOpen}
                />
                <NotificationDropdown
                  isOpen={notificationsOpen}
                  onClose={() => setNotificationsOpen(false)}
                />
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-label={t('nav.accountMenu', 'Account menu')}
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                  className="flex items-center gap-1.5 sm:gap-2 p-1 sm:p-1.5 hover:bg-primary-50/80 dark:hover:bg-primary-950/30 rounded-xl transition-all duration-200 group"
                >
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt=""
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-full ring-2 ring-transparent group-hover:ring-primary-200 dark:group-hover:ring-primary-800 transition-all"
                    />
                  ) : (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-primary-500 rounded-full flex items-center justify-center ring-2 ring-transparent group-hover:ring-primary-200 dark:group-hover:ring-primary-800 transition-all">
                      <span className="text-white font-semibold text-xs sm:text-sm">
                        {user?.displayName?.charAt(0).toUpperCase() ?? user?.email?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <svg className="hidden sm:block w-4 h-4 text-neutral-500 dark:text-neutral-400 transition-transform group-hover:rotate-180 duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {userMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-[55]"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    {/* Menu */}
                    <div className="absolute right-0 mt-2 sm:mt-3 w-56 sm:w-64 glass-effect-strong rounded-xl sm:rounded-2xl shadow-soft-lg border border-neutral-200/50 dark:border-neutral-700/50 overflow-hidden z-[60] animate-scale-in">
                      {/* User info */}
                      <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-neutral-200/50 dark:border-neutral-700/50 bg-primary-50/30 dark:bg-primary-950/20">
                        <div className="text-sm font-semibold text-neutral-900 dark:text-white truncate">
                          {user?.displayName}
                        </div>
                        <div className="text-xs text-neutral-600 dark:text-neutral-400 truncate mt-0.5">{user?.email}</div>
                        {currentOrganization && (
                          <div className="mt-2 sm:mt-2.5 px-2 sm:px-2.5 py-1 bg-white/60 dark:bg-neutral-800/60 rounded-lg text-xs font-medium text-neutral-700 dark:text-neutral-300 inline-flex items-center gap-1.5 max-w-full">
                            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <span className="truncate">{currentOrganization.name}</span>
                          </div>
                        )}
                      </div>

                      {/* Menu items */}
                      <div className="py-1.5 sm:py-2 px-1.5 sm:px-2">
                        <Link
                          to="/settings/profile"
                          onClick={() => setUserMenuOpen(false)}
                          className="block px-3 py-3 sm:py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-primary-50/80 dark:hover:bg-primary-950/30 hover:text-primary-700 dark:hover:text-primary-400 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
                        >
                          {t('nav.profile')}
                        </Link>
                        <Link
                          to="/settings/account"
                          onClick={() => setUserMenuOpen(false)}
                          className="block px-3 py-3 sm:py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-primary-50/80 dark:hover:bg-primary-950/30 hover:text-primary-700 dark:hover:text-primary-400 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
                        >
                          {t('nav.settings')}
                        </Link>
                        <Link
                          to="/settings/organization"
                          onClick={() => setUserMenuOpen(false)}
                          className="block px-3 py-3 sm:py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-primary-50/80 dark:hover:bg-primary-950/30 hover:text-primary-700 dark:hover:text-primary-400 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
                        >
                          {t('nav.organization')}
                        </Link>
                      </div>

                      <div className="border-t border-neutral-200/50 dark:border-neutral-700/50 py-1.5 sm:py-2 px-1.5 sm:px-2">
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-3 py-3 sm:py-2.5 text-sm font-medium text-accent-600 dark:text-accent-400 hover:bg-accent-50/80 dark:hover:bg-accent-950/30 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
                        >
                          {t('nav.logout')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <MobileNav isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
    </>
  );
}
